import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Popover,
  Link,
  Button,
  styled as muiStyled,
} from "@mui/material";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import styled from "@emotion/styled";
import { format } from "date-fns";
import StatusDropDown from "../../../common/components/StatusDropDown";
import { useUpdateStudentQuery } from "../../../api/useUpdateStudent";
import { useQueryClient } from "@tanstack/react-query";

ChartJS.register(ArcElement, Tooltip, Legend);

const ChartContainer = styled(Box)({
  width: "100%",
  maxWidth: "400px",
  margin: "0 auto",
  padding: "16px",
});

const RiskListContainer = styled(Paper)({
  marginTop: "24px",
  padding: "16px",
});

const RiskSection = styled(Box)({
  marginTop: "16px",
  "&:first-of-type": {
    marginTop: 0,
  },
});

const StudentList = styled(Box)({
  marginTop: "8px",
  "& > *": {
    marginBottom: "4px",
  },
});

const StudentName = muiStyled(Typography)(({ theme }) => ({
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const TooltipContent = styled(Box)({
  padding: "16px",
  maxWidth: "400px",
  width: "400px",
});

const AssignmentList = styled(Box)({
  marginTop: "8px",
  width: "100%",
});

const AssignmentItem = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "12px",
  marginBottom: "8px",
  width: "100%",
  "& > *:first-of-type": {
    width: "80px",
    flexShrink: 0,
  },
  "& > *:nth-of-type(2)": {
    width: "160px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "0.75rem",
    flexShrink: 0,
  },
});

const SubmissionLink = styled(Link)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "4px 8px",
  borderRadius: "4px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const SubmissionList = styled(Box)({
  marginTop: "4px",
  marginLeft: "92px",
  width: "calc(100% - 92px)",
  "& > *": {
    marginBottom: "4px",
  },
});

const ActiveStatusPie = ({ students }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isStatusChanged, setIsStatusChanged] = useState(false);
  const { mutate: updateStudent } = useUpdateStudentQuery();
  const queryClient = useQueryClient();

  const handleClick = (event, student) => {
    setAnchorEl(event.currentTarget);
    setSelectedStudent(student);
    setSelectedStatus(student.status);
    setIsStatusChanged(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedStudent(null);
    setSelectedStatus("");
    setIsStatusChanged(false);
  };

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    setSelectedStatus(newStatus);
    setIsStatusChanged(newStatus !== selectedStudent?.status);
  };

  const handleUpdateStatus = () => {
    if (selectedStudent && selectedStatus) {
      updateStudent(
        { ...selectedStudent, status: selectedStatus },
        {
          onSuccess: () => {
            setIsStatusChanged(false);
            handleClose();
            queryClient.invalidateQueries(["students"]);
          },
          onError: (error) => {
            console.error("상태 업데이트 실패:", error);
          },
        }
      );
    }
  };

  const open = Boolean(anchorEl);

  const { statusData, riskStudents, criticalStudents } = useMemo(() => {
    if (!students || students.length === 0)
      return { statusData: null, riskStudents: [], criticalStudents: [] };

    // 파이 차트 데이터 계산
    const statusCounts = students.reduce(
      (acc, student) => {
        const status = student.status || "unknown";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      { active: 0, fail: 0, "give up": 0, drop: 0 }
    );

    // 탈락 위기 학생 (fail 또는 미제출 1번) 필터링
    const riskStudents = students.filter((student) => {
      if (student.status !== "active") return false;

      const failCount = student.failAssignments?.length || 0;
      const missedCount = student.missedAssignments?.length || 0;

      return failCount === 1 || missedCount === 1;
    });

    // 탈락 예정 학생 (fail 또는 미제출 2번 이상) 필터링
    const criticalStudents = students.filter((student) => {
      if (student.status !== "active") return false;

      const failCount = student.failAssignments?.length || 0;
      const missedCount = student.missedAssignments?.length || 0;

      return failCount >= 2 || missedCount >= 2;
    });

    return {
      statusData: {
        labels: ["활성", "실패", "포기", "탈락"],
        datasets: [
          {
            data: [
              statusCounts.active || 0,
              statusCounts.fail || 0,
              statusCounts["give up"] || 0,
              statusCounts.drop || 0,
            ],
            backgroundColor: ["#4caf50", "#f44336", "#2196f3", "#9e9e9e"],
            borderColor: ["#388e3c", "#d32f2f", "#1976d2", "#757575"],
            borderWidth: 1,
          },
        ],
      },
      riskStudents,
      criticalStudents,
    };
  }, [students]);

  const options = {
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: {
            size: 14,
          },
          padding: 20,
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value}명 (${percentage}%)`;
          },
        },
      },
    },
    maintainAspectRatio: true,
    responsive: true,
  };

  const renderStudentTooltip = () => {
    if (!selectedStudent) return null;

    const failedAssignments = selectedStudent.failAssignments || [];
    const missedAssignments = selectedStudent.missedAssignments || [];

    return (
      <TooltipContent>
        <Typography variant="subtitle2" gutterBottom>
          과제 현황
        </Typography>

        {failedAssignments.length > 0 && (
          <AssignmentList>
            <Typography variant="body2" color="error" gutterBottom>
              실패한 과제:
            </Typography>
            {failedAssignments.map((assignment) => (
              <AssignmentItem key={assignment._id}>
                <Typography variant="body2">
                  {format(new Date(assignment.closeDate), "yyyy.MM.dd")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {assignment.assignment}
                </Typography>
                {assignment.submission?.submissionLinks &&
                  assignment.submission.submissionLinks.length > 0 && (
                    <SubmissionList>
                      {assignment.submission.submissionLinks.map((link) => (
                        <SubmissionLink
                          key={link._id}
                          component="a"
                          href={link.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          underline="hover"
                        >
                          {link.title}
                        </SubmissionLink>
                      ))}
                    </SubmissionList>
                  )}
              </AssignmentItem>
            ))}
          </AssignmentList>
        )}

        {missedAssignments.length > 0 && (
          <AssignmentList>
            <Typography variant="body2" color="warning.main" gutterBottom>
              미제출 과제:
            </Typography>
            {missedAssignments.map((assignment) => (
              <AssignmentItem key={assignment._id}>
                <Typography variant="body2">
                  {format(new Date(assignment.closeDate), "yyyy.MM.dd")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {assignment.assignment}
                </Typography>
              </AssignmentItem>
            ))}
          </AssignmentList>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            상태 변경:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ minWidth: 120 }}>
              <StatusDropDown
                selectedStatus={selectedStatus}
                handleStatusChange={handleStatusChange}
              />
            </Box>
            {isStatusChanged && (
              <Button
                variant="contained"
                size="small"
                onClick={handleUpdateStatus}
                color="primary"
                sx={{ ml: 1 }}
              >
                변경
              </Button>
            )}
          </Box>
        </Box>
      </TooltipContent>
    );
  };

  const renderStudentList = (students) => (
    <StudentList>
      {students.map((student) => (
        <StudentName
          key={student._id}
          variant="body2"
          onClick={(e) => handleClick(e, student)}
        >
          @{student.userInfo?.name || student.userId.name}
        </StudentName>
      ))}
      {students.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          해당하는 학생이 없습니다
        </Typography>
      )}
    </StudentList>
  );

  if (!statusData) {
    return <Typography>데이터가 없습니다</Typography>;
  }

  return (
    <Box>
      <ChartContainer>
        <Typography variant="h6" align="center" gutterBottom>
          학생 상태 분포
        </Typography>
        <Pie data={statusData} options={options} />
      </ChartContainer>

      <RiskListContainer elevation={2}>
        <Typography variant="h6" gutterBottom>
          관리가 필요한 학생
        </Typography>

        <RiskSection>
          <Typography variant="subtitle1" color="error">
            탈락 예정 ({criticalStudents.length}명)
          </Typography>
          {renderStudentList(criticalStudents)}
        </RiskSection>

        <Divider sx={{ my: 2 }} />

        <RiskSection>
          <Typography variant="subtitle1" color="warning.main">
            탈락 위기 ({riskStudents.length}명)
          </Typography>
          {renderStudentList(riskStudents)}
        </RiskSection>
      </RiskListContainer>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {renderStudentTooltip()}
      </Popover>
    </Box>
  );
};

export default ActiveStatusPie;
